import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Grid,
    Input
} from "@mui/material"
import {
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarExport
} from "@mui/x-data-grid"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import { ButtonStyles, DialogStyles } from "../styles/MaintenanceStyles"
import {
    createTool,
    getTools,
    deleteTool,
    updateTool,
    updateselected,
    setAlertDialog,
    setAlertText
} from "../../../actions/maintenanceActions"

function ToolToolBar() {
    const dispatch = useDispatch()

    // Store Variables
    const maintenance = useSelector((state) => state.maintenance)
    const auth = useSelector((state) => state.auth)
    const permissions = useSelector((state) => state.auth.permissions)

    // Add Dialog State
    const [addDialogValues, setAddDialogValues] = useState({
        Tool: ""
    })
    const [addDialogOpen, setAddDialogOpen] = useState(false)

    //edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [editDialogValues, setEditDialogValues] = useState({
        Tool: ""
    })

    // Delete Dialog State
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

    // Alert Handlers
    const handleAlertDialogClose = (event, reason) => {
        if (reason === "clickaway") {
            return
        }
        dispatch(setAlertDialog(false))
    }

    // Add Dialog Handlers
    const handleAddDialogOpen = () => {
        clearAddDialogValues()
        setAddDialogOpen(true)
        handleAlertDialogClose(null, null)
    }

    const handleAddDialogClose = () => {
        clearAddDialogValues()
        setAddDialogOpen(false)
    }

    const clearAddDialogValues = () => {
        setAddDialogValues({ Tool: "" })
    }

    const handleConfirmAdd = async () => {
        await dispatch(getTools())
        if (addDialogValues.Tool !== "") {
            if (
                maintenance.tools?.filter(
                    (Tool) =>
                        Tool.Tool.toLowerCase() ===
                        addDialogValues.Tool.toLowerCase()
                ).length > 0
            ) {
                dispatch(setAlertText("This Tool already exists"))
                dispatch(setAlertDialog(true))
            } else {
                try {
                    addDialogValues.Tool = addDialogValues.Tool.trim()
                    const newTool = {
                        Tool: addDialogValues.Tool,
                        AuditLog: {
                            Action: "Create Tool",
                            OldValue: "",
                            NewValue: addDialogValues.Tool,
                            AssociateOID: auth.userData.AssociateOID
                        }
                    }
                    await dispatch(createTool(newTool))
                    await dispatch(getTools())
                    setAddDialogOpen(false)
                    clearAddDialogValues()
                } catch (error) {
                    console.log(error)
                    clearAddDialogValues()
                }
            }
        } else {
            dispatch(setAlertText("Tool Name field must be filled out"))
            dispatch(setAlertDialog(true))
        }
    }

    const handleUpdateAddDialogValues = (e) => {
        const name = e.target.name
        const value = e.target.value

        setAddDialogValues({
            ...addDialogValues,
            [name]: value
        })
    }

    // Delete Dialog Handlers
    const handleDeleteDialogOpen = () => {
        if (maintenance.selected.ToolId) {
            setAddDialogValues({
                Tool: maintenance.selected.Tool
            })
            setDeleteDialogOpen(true)
        }
    }

    const handleDeleteDialogClose = () => {
        clearAddDialogValues()
        setDeleteDialogOpen(false)
    }

    const handleConfirmDelete = async () => {
        try {
            const deleteMethod = {
                ToolId: maintenance.selected.ToolId,
                AuditLog: {
                    Action: "Delete Tool",
                    OldValue: maintenance.selected.Tool,
                    NewValue: "",
                    AssociateOID: auth.userData.AssociateOID
                }
            }
            await dispatch(deleteTool(deleteMethod))
            await dispatch(getTools())
            dispatch(updateselected([]))
            setDeleteDialogOpen(false)
        } catch (error) {
            await dispatch(getTools())
            dispatch(updateselected([]))
            setDeleteDialogOpen(false)
        }
    }

    //Edit Dialog Handlers
    const handleEditDialogOpen = () => {
        if (maintenance.selected.ToolId) {
            setEditDialogValues({
                Tool: maintenance.selected.Tool
            })
            setEditDialogOpen(true)
        }
    }

    const handleEditDialogClose = () => {
        clearAddDialogValues()
        setEditDialogOpen(false)
    }

    const handleConfirmEdit = async () => {
        if (editDialogValues.Tool !== "") {
            if (
                maintenance.tools?.filter(
                    (Tool) =>
                        Tool.Tool.toLowerCase() ===
                        editDialogValues.Tool.toLowerCase()
                ).length > 0
            ) {
                dispatch(setAlertText("This Tool already exists"))
                dispatch(setAlertDialog(true))
            } else {
                try {
                    editDialogValues.Tool = editDialogValues.Tool.trim()
                    const updatedTool = {
                        ToolId: maintenance.selected.ToolId,
                        Tool: editDialogValues.Tool
                    }
                    const newTool = {
                        Tool: updatedTool,
                        AuditLog: {
                            ToolId: maintenance.selected.ToolId,
                            Action: "Update Tool",
                            OldValue: maintenance.selected.Tool,
                            NewValue: editDialogValues.Tool,
                            AssociateOID: auth.userData.AssociateOID
                        }
                    }
                    await dispatch(updateTool(newTool))
                    dispatch(updateselected([updatedTool]))
                    await dispatch(getTools())
                    setEditDialogOpen(false)
                    clearAddDialogValues()
                } catch (error) {
                    console.log(error)
                    clearAddDialogValues()
                }
            }
        } else {
            dispatch(setAlertText("Tool Name field must be filled out"))
            dispatch(setAlertDialog(true))
        }
    }

    const handleUpdateEditDialogValues = (e) => {
        const name = e.target.name
        const value = e.target.value

        setEditDialogValues({
            ...editDialogValues,
            [name]: value
        })
    }

    return (
        <Grid>
            {/*ToolBar*/}
            <GridToolbarContainer sx={ButtonStyles}>
                <GridToolbarFilterButton sx={ButtonStyles} />
                <GridToolbarDensitySelector sx={ButtonStyles} />
                <GridToolbarExport sx={ButtonStyles} />
                {permissions.EditMaintenance && (
                    <>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={handleAddDialogOpen}
                            sx={ButtonStyles}
                            disabled={maintenance.isBeingEdited}
                        >
                            Add
                        </Button>
                        {maintenance.selected.ToolId && (
                            <>
                                <Button
                                    startIcon={<EditIcon />}
                                    onClick={handleEditDialogOpen}
                                    sx={ButtonStyles}
                                    disabled={maintenance.isBeingEdited}
                                >
                                    Edit
                                </Button>
                                <Button
                                    startIcon={<DeleteIcon />}
                                    onClick={handleDeleteDialogOpen}
                                    sx={ButtonStyles}
                                    disabled={maintenance.isBeingEdited}
                                >
                                    Delete
                                </Button>
                            </>
                        )}
                    </>
                )}
            </GridToolbarContainer>

            {/*Add Tool Dialog*/}
            <Dialog
                open={addDialogOpen}
                onClose={handleAddDialogClose}
                sx={DialogStyles}
            >
                <DialogTitle>Add Tool</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={DialogStyles}>
                        Please enter the Tool you would like to add
                    </DialogContentText>
                    <Input
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Tool Name"
                        onChange={handleUpdateAddDialogValues}
                        name="Tool"
                        sx={DialogStyles}
                        value={addDialogValues.Tool}
                        fullWidth
                        variant="standard"
                        placeholder="Tool Name"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmAdd}
                        sx={DialogStyles}
                    >
                        Add
                    </Button>
                    <Button
                        onClick={handleAddDialogClose}
                        sx={DialogStyles}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/*Delete Tool Dialog*/}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteDialogClose}
                sx={DialogStyles}
            >
                <DialogTitle>Delete Tool</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={DialogStyles}>
                        Are you sure you would like to delete{" "}
                        {addDialogValues.Tool}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmDelete}
                        sx={DialogStyles}
                    >
                        Yes
                    </Button>
                    <Button
                        onClick={handleDeleteDialogClose}
                        sx={DialogStyles}
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>

            {/*Edit Tool Dialog*/}
            <Dialog
                open={editDialogOpen}
                onClose={handleEditDialogClose}
                sx={DialogStyles}
            >
                <DialogTitle>Edit Tool</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={DialogStyles}>
                        What would you like to change the tool to?
                    </DialogContentText>
                    <Input
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Tool Name"
                        onChange={handleUpdateEditDialogValues}
                        name="Tool"
                        sx={DialogStyles}
                        value={editDialogValues.Tool}
                        fullWidth
                        variant="standard"
                        placeholder="Tool Name"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmEdit}
                        sx={DialogStyles}
                    >
                        Confirm
                    </Button>
                    <Button
                        onClick={handleEditDialogClose}
                        sx={DialogStyles}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}

export default ToolToolBar
